import {
  Badge,
  Box,
  Button,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Text,
} from '@optiaxiom/react';
import { IconXmark } from '@optimizely/axiom-icons';

import { ArtifactContainer, ArtifactProvider, Artifacts } from '@frontend/components/Artifact';
import { MarkdownText } from '@frontend/components/MarkdownText';
import { useInstanceId } from '@frontend/hooks';
import { SpecializedAgentExecution } from '@frontend/types/specialized-agent';

import {
  useGetAgentMemoryChunks,
  useGetSpecializedAgent,
  useGetSpecializedAgentExecution,
} from '../../../hooks/useSpecializedAgents';
import { StopExecutionButton } from '../../shared/StopExecutionButton';
import { TokenUsageSection } from '../../shared/TokenUsageSection';
import { EmptyStateMessage } from '../shared/EmptyStateMessage';
import { ExecutionDetailsCard } from '../shared/ExecutionDetailsCard';
import { ExecutionOutputDisplay } from '../shared/ExecutionOutputDisplay';
import { ExecutionStatusBadge } from '../shared/ExecutionStatusBadge';
import { MemoryChunksDisplay } from '../shared/MemoryChunksDisplay';

import { styles } from './SpecializedAgentExecutionDetailsSidebar.css';

type SpecializedAgentExecutionDetailsSidebarProps = {
  executionId?: string;
  execution?: SpecializedAgentExecution;
  onClose: () => void;
};

export function SpecializedAgentExecutionDetailsSidebar({
  executionId,
  execution: providedExecution,
  onClose,
}: SpecializedAgentExecutionDetailsSidebarProps) {
  const { instanceId } = useInstanceId();

  // Fetch execution details only if no execution is provided and executionId exists
  const shouldFetchExecution = !providedExecution && !!executionId && !!instanceId;
  const {
    execution: fetchedExecution,
    isPending: isLoadingExecution,
    error: executionError,
  } = useGetSpecializedAgentExecution(
    shouldFetchExecution ? instanceId : '',
    shouldFetchExecution ? executionId : ''
  );

  const execution = providedExecution || fetchedExecution;
  const isStreaming = execution
    ? execution?.status.toLowerCase() !== 'completed' &&
      execution?.status.toLowerCase() !== 'failed'
    : false;

  const { agent } = useGetSpecializedAgent(execution?.links?.agent || '');

  const { memoryChunks } = useGetAgentMemoryChunks(
    execution?.memory_id,
    execution?.links?.memory,
    isStreaming
  );

  const formatJsonForMarkdown = (obj: unknown): string => {
    return '```json\n' + JSON.stringify(obj, null, 2) + '\n```';
  };

  if (isLoadingExecution && !providedExecution) {
    return (
      <Flex flexDirection="column" gap="16" p="16">
        <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
          <Text fontWeight="600" fontSize="lg">
            Loading Execution...
          </Text>
          <Button
            icon={<IconXmark size="16" />}
            appearance="default"
            onClick={() => {
              onClose();
            }}
            aria-label="Close sidebar"
          />
        </Flex>
        <Flex justifyContent="center" alignItems="center" h="224">
          <Text>Loading execution details...</Text>
        </Flex>
      </Flex>
    );
  }

  if (executionError || !execution) {
    return (
      <Flex flexDirection="column" gap="16" p="16">
        <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
          <Text fontWeight="600" fontSize="lg">
            Execution Details
          </Text>
          <Button
            icon={<IconXmark size="16" />}
            appearance="default"
            onClick={onClose}
            aria-label="Close sidebar"
          />
        </Flex>
        <EmptyStateMessage message="Failed to load execution details" />
      </Flex>
    );
  }

  return (
    <ArtifactProvider>
      <ArtifactContainer>
        <Flex flexDirection="column" gap="16" p="16">
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            className={styles.header}
          >
            <Flex flexDirection="row" alignItems="center" gap="12">
              <Text fontWeight="600" fontSize="lg">
                Execution Details
              </Text>
              <ExecutionStatusBadge status={execution.status} showAsRunning />
            </Flex>
            <Flex flexDirection="row" gap="8" alignItems="center">
              {isStreaming && executionId && <StopExecutionButton executionId={executionId} />}
              <Button
                icon={<IconXmark size="16" />}
                appearance="default"
                onClick={onClose}
                aria-label="Close sidebar"
              />
            </Flex>
          </Flex>

          {/* Basic Info */}
          <ExecutionDetailsCard
            executionId={execution.id}
            status={execution.status}
            agentName={agent?.name ? `${agent.name}` : undefined}
            createdAt={execution.created_at}
            executionTime={execution.execution_time}
            tokenUsage={execution.token_usage}
            evaluationScore={execution.evaluation_score}
            baselineEvaluationScore={agent?.baseline_evaluation_score}
            variant="detailed"
            hideStatusBadge
          />

          {/* Input Variables */}
          <Disclosure defaultOpen>
            <DisclosureTrigger>
              <Field label="Input Variables" info="Parameters used for this execution" />
            </DisclosureTrigger>
            <DisclosureContent>
              <Flex flexDirection="column" gap="16" px="16">
                {execution.parameters && Object.keys(execution.parameters).length > 0 ? (
                  <Field label="Variables">
                    <Box border="1" borderColor="border.default" rounded="md">
                      <Box bg="bg.default" p="12">
                        <MarkdownText text={formatJsonForMarkdown(execution.parameters)} />
                      </Box>
                    </Box>
                  </Field>
                ) : (
                  <EmptyStateMessage message="No parameters were used for this execution" />
                )}
              </Flex>
            </DisclosureContent>
          </Disclosure>

          {/* Artifact Disclosure */}
          {execution.artifacts && execution.artifacts.length > 0 && (
            <Disclosure w="full" defaultOpen>
              <DisclosureTrigger>
                <Field label="Artifacts" info="Agent Execution Artifacts:" />
              </DisclosureTrigger>
              <DisclosureContent>
                <Artifacts artifacts={execution?.artifacts} />
              </DisclosureContent>
            </Disclosure>
          )}
          {/* Output/Result */}
          <Disclosure defaultOpen>
            <DisclosureTrigger>
              <Field label="Output" info="Execution results and response" />
            </DisclosureTrigger>
            <DisclosureContent>
              <Flex flexDirection="column" gap="16" px="16">
                <Field label="Agent Response">
                  <ExecutionOutputDisplay
                    result={execution.result}
                    showStatus={false}
                    emptyMessage="No output available."
                    emptySubMessage="Execution did not produce any results."
                  />
                </Field>
              </Flex>
            </DisclosureContent>
          </Disclosure>

          {/* Execution Memory */}
          {execution.links?.memory && (
            <Disclosure defaultOpen>
              <DisclosureTrigger>
                <Field label="Execution Memory" info="Agent memory chunks from execution" />
              </DisclosureTrigger>
              <DisclosureContent>
                <Flex flexDirection="column" gap="16">
                  <MemoryChunksDisplay
                    memoryChunks={memoryChunks || []}
                    isStreaming={isStreaming}
                    emptyMessage="No memory chunks available"
                    emptySubMessage="Memory data will appear here when available."
                  />
                </Flex>
              </DisclosureContent>
            </Disclosure>
          )}

          {/* Quality Score */}
          {execution.evaluation_score !== null && execution.evaluation_score !== undefined && (
            <Disclosure>
              <DisclosureTrigger>
                <Field
                  label={
                    <Flex flexDirection="row" alignItems="center" gap="8">
                      <Text>Quality Score: {execution.evaluation_score.toFixed(0)}%</Text>
                      <Badge
                        intent={
                          execution.evaluation_score >= (agent?.baseline_evaluation_score ?? 70)
                            ? 'success'
                            : 'danger'
                        }
                      >
                        {execution.evaluation_score >= (agent?.baseline_evaluation_score ?? 70)
                          ? 'Passed'
                          : 'Failed'}
                      </Badge>
                    </Flex>
                  }
                />
              </DisclosureTrigger>
              <DisclosureContent>
                <Flex flexDirection="column" gap="12" px="16">
                  {agent?.baseline_evaluation_score !== undefined && (
                    <Flex flexDirection="row" alignItems="center" gap="4">
                      <Field
                        label="Baseline Quality"
                        info="The lowest acceptable quality score for this agent's response"
                      />
                      <Text>: {agent.baseline_evaluation_score.toFixed(0)}%</Text>
                    </Flex>
                  )}
                  {execution.evaluation_feedback && (
                    <Field label="Evaluation Feedback">
                      <Text>{execution.evaluation_feedback}</Text>
                    </Field>
                  )}
                </Flex>
              </DisclosureContent>
            </Disclosure>
          )}

          {/* Token Usage */}
          <TokenUsageSection tokenUsage={execution.token_usage} />
        </Flex>
      </ArtifactContainer>
    </ArtifactProvider>
  );
}
